import Icon from "@web-ui/components/Icon";
import Logo from "@web-ui/components/Logo";
import ThemeButton from "@web-ui/components/ThemeButton";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { BRANDS, type Brand } from "@/api/client";
import { BrandMark } from "@/components/brand-mark";
import { href } from "@/lib/route";
import { lower, noun, packFor } from "@/vocabulary/vocabulary";

/**
 * The frame of the sign-in screen, as the AnyTV provider console's PublicFrame
 * (provider-portal/features/auth/public-frame.tsx): on large screens a brand panel in the brand's
 * accent on the left (the mark and name, a headline, three plain points about what the console
 * does, the support line), the form on the right in a panel of the same shape with a subtle
 * stroke. The page's h1 is the form's title; the panel is decoration and hides on small screens,
 * where the brand's mark sits above the form.
 */
export function PublicFrame({ brand, title, description, children }: { brand: Brand; title: string; description?: string; children: React.ReactNode }) {
  const pack = packFor(brand.vertical);
  const items = lower(noun(pack, "title", "many"));
  const points = [
    `Add ${items} with their video and artwork.`,
    "Arrange the rows on every screen of your app.",
    "See each change before it goes live, then publish in one step.",
  ];
  return (
    <div className="grid min-h-svh gap-4 p-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(28rem,0.6fr)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden rounded-4xl bg-accent p-10 text-on-accent lg:flex" aria-hidden="true">
        <div className="pointer-events-none absolute -end-32 -top-32 size-96 rounded-full bg-on-accent/10" />
        <div className="pointer-events-none absolute -bottom-40 -start-24 size-[28rem] rounded-full bg-on-accent/5" />
        <div className="relative flex items-center gap-3">
          <span className="flex size-12 items-center justify-center rounded-2xl bg-on-accent/15 text-h5 font-semibold text-on-accent">{brand.name.slice(0, 1)}</span>
          <span className="text-sub-title-1">{brand.name} Studio Console</span>
        </div>
        <div className="relative max-w-xl">
          <p className="text-body-2 text-on-accent/70">For the {brand.name} team</p>
          <p className="mt-4 text-[2.5rem] font-medium leading-[1.15] tracking-[-0.03em] text-balance max-xl:text-[2rem]">Shape the {brand.name} app on phones and TVs, and choose when changes go live.</p>
          <ul className="mt-10 flex flex-col gap-4">
            {points.map((point) => (
              <li className="flex items-center gap-3 text-body-1 text-on-accent/90" key={point}>
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-on-accent/15">
                  <Icon className="fill-on-accent" name="check" size={4} />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative flex gap-5 text-caption text-on-accent/70">
          <span>{brand.name}</span>
          <span>{brand.legal.supportEmail}</span>
        </div>
      </aside>
      <main className="relative flex items-center justify-center rounded-4xl border border-s-subtle bg-b-surface2 px-8 py-12 max-md:border-0 max-md:bg-transparent max-md:px-1 max-md:py-8">
        <div className="w-full max-w-md">
          <Logo className="mb-8 lg:hidden" href={href({ brand: brand.id, section: "home" })}>
            <BrandMark brand={brand} current size="lg" />
            <span className="sr-only">{brand.name}</span>
          </Logo>
          <h1 className="text-h4 max-md:text-h5">{title}</h1>
          <p className="mt-2 mb-8 text-body-2 text-t-secondary">{description ?? `${brand.name} Studio Console`}</p>
          <div className="[&_form>*:first-child]:!mt-0 [&>*:first-child]:!mt-0">{children}</div>
          <nav aria-label="More" className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-caption text-t-secondary">
            {BRANDS.length > 1 ? <OtherBrands current={brand} /> : null}
            <a className="transition-colors hover:text-t-primary" href={brand.legal.termsUrl} target="_blank" rel="noreferrer">Terms</a>
            <a className="transition-colors hover:text-t-primary" href={brand.legal.privacyUrl} target="_blank" rel="noreferrer">Privacy</a>
            <a className="transition-colors hover:text-t-primary" href={`mailto:${brand.legal.supportEmail}`}>Help</a>
          </nav>
        </div>
        <ThemeButton className="absolute end-6 bottom-6 border border-s-subtle max-lg:hidden" />
      </main>
    </div>
  );
}

/** Signing in is per brand: staff of several brands pick the one they want here. */
function OtherBrands({ current }: { current: Brand }) {
  return (
    <Menu as="div" className="relative">
      <MenuButton className="flex items-center gap-1 transition-colors hover:text-t-primary data-[open]:text-t-primary">
        Another brand <Icon name="chevron" size={4} className="fill-current" />
      </MenuButton>
      <MenuItems className="menu w-64" anchor="top" transition>
        {BRANDS.filter((b) => b.id !== current.id).map((b) => (
          <MenuItem key={b.id}>
            <a className="menu__item gap-3" href={href({ brand: b.id, section: "home" })}>
              <BrandMark brand={b} size="sm" />
              <span className="text-body-2">{b.name}</span>
            </a>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
